import { useCallback, useContext, useState, useEffect } from "react"
import type { FileRejection } from "react-dropzone"
import { ErrorCode, useDropzone } from "react-dropzone"
import { FaTrash } from "react-icons/fa"
import { TbFileUpload } from "react-icons/tb"

import partsListApi from "../../../api/partsListApi"
import LINKS from "../../../constant/links"
import OneClickEstimateContext from "../../../OneClickEstimateContext"
import { useSearchParams } from "next/navigation"

const MAX_FILE_SIZE = 10 * 1024 * 1024
/** localStorageMapkeyを持ち回るsessionStorageのキー */
const SESSION_STORAGE_KEY = "partsListUpload"

type Props = {
  validationError?: string
}

const PartsListUpload = ({ validationError }: Props) => {
  const { partsListMapkey, setPartsListMapkey } = useContext(OneClickEstimateContext)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!partsListMapkey) {
      setUploadedFileName(null)
      setErrorMessage(null)
    }
  }, [partsListMapkey])

  // ページ離脱時にsessionStorageをクリアし、別ページから戻った際に古いファイル情報が残らないようにする
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [])

  // 画面ロード時にクエリパラメータを参照し、一時保存しているアップロードファイルがある場合は初期表示する
  useEffect(() => {
    const localStorageMapkey = searchParams.get("localStorageMapkey")
    const loadExistingFile = async () => {
      try {
        if (!localStorageMapkey) {
          return
        }
        setIsUploading(true)
        // APIから既存のファイル情報を取得
        const result = await partsListApi.fileInfo(localStorageMapkey)

        if (result.isSuccess && result.data) {
          const { fileName } = result.data
          setUploadedFileName(fileName)
          setPartsListMapkey(localStorageMapkey)
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ fileName, localStorageMapkey }))
        }
      } catch (error) {
        setErrorMessage("ファイルの取得に失敗しました")
      } finally {
        setIsUploading(false)
      }
    }

    loadExistingFile().catch(() => {
      setErrorMessage("ファイル名の取得に失敗しました")
    })
  }, [searchParams, setPartsListMapkey])

  // history.backで戻った場合は、sessionStorageからアップロードファイルを復元
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)

    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (typeof parsed === "object" && parsed?.fileName && parsed?.localStorageMapkey) {
          setUploadedFileName(parsed.fileName)
          setPartsListMapkey(parsed.localStorageMapkey)
        }
      } catch (e) {
        setErrorMessage("ファイルの取得に失敗しました")
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
      }
    }
  }, [setPartsListMapkey])

  const handleRemoveFile = useCallback(() => {
    setUploadedFileName(null)
    setErrorMessage(null)
    setPartsListMapkey("")
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  }, [setPartsListMapkey])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
    onFileDialogOpen: () => setErrorMessage(null),
    onDragEnter: () => setErrorMessage(null),
    onDrop: async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const firstRejection = fileRejections[0]
      if (firstRejection?.errors?.[0]) {
        const error = firstRejection.errors[0]

        switch (error.code) {
          case ErrorCode.TooManyFiles:
            setErrorMessage("アップロード出来るファイルは1つまでです。")
            return
          case ErrorCode.FileInvalidType:
            setErrorMessage("拡張子 .xls / .xlsx のファイルを選択してください。")
            return
          case ErrorCode.FileTooLarge:
            setErrorMessage("ファイル容量は10MBまでです。")
            return
          default:
            setErrorMessage("ファイルのアップロードに失敗しました。")
            return
        }
      }

      const firstFile = acceptedFiles[0]
      if (firstFile) {
        setIsUploading(true)
        setErrorMessage(null)

        try {
          const result = await partsListApi.upload(firstFile)

          if (result.isSuccess) {
            const mapkey = result.data.localStorageMapkey
            setUploadedFileName(firstFile.name)
            setPartsListMapkey(mapkey)
            sessionStorage.setItem(
              SESSION_STORAGE_KEY,
              JSON.stringify({ fileName: firstFile.name, localStorageMapkey: mapkey }),
            )
          } else {
            setErrorMessage(result.data.message || "ファイルのアップロードに失敗しました。")
          }
        } catch (error) {
          console.error("File upload error:", error)
          setErrorMessage("ファイルのアップロードに失敗しました。")
        } finally {
          setIsUploading(false)
        }
      }
    },
  })

  return (
    <>
      <div className="mt-8 flex flex-col gap-4">
        <h4 className="text-base font-bold leading-[1.5] tracking-[0.15px] text-[#32383e]">部品リストをアップロード</h4>

        {uploadedFileName ? (
          <div className="flex w-full items-center justify-between rounded bg-[#FBFCFE] px-4 py-3">
            <div className="flex items-center gap-4">
              <TbFileUpload className="h-5 w-5 text-[#16a34a]" />
              <span className="text-sm text-[#32383e]">{uploadedFileName}</span>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="flex h-8 w-8 items-center justify-center rounded-full text-[#9fa6ad] transition-colors hover:bg-gray-100 hover:text-[#32383e]"
              aria-label="ファイルを削除"
              disabled={isUploading}
            >
              <FaTrash className="h-4 w-4" color="#636B74" />
            </button>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded border border-dashed px-4 py-6 transition-colors ${
              isUploading
                ? "cursor-wait border-[#9fa6ad] bg-gray-100"
                : isDragActive
                  ? "border-[#51bc51] bg-[#f0fdf4]"
                  : errorMessage
                    ? "border-red-400 bg-red-50"
                    : "border-[#9fa6ad] bg-[#fbfcfe]"
            }`}
          >
            <input {...getInputProps()} disabled={isUploading} />
            <div className="relative flex h-10 w-10 items-center justify-center rounded-full">
              {isUploading ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#16a34a] border-t-transparent" />
              ) : (
                <TbFileUpload className={`h-6 w-6 ${errorMessage ? "text-red-500" : "text-[#16a34a]"}`} />
              )}
            </div>
            {isUploading ? (
              <p className="text-sm text-[#32383e]">アップロード中...</p>
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <a
                    href={LINKS.PARTS_LIST_TEMPLATE}
                    download="parts_list.xlsx"
                    className="text-sm leading-[1.42] text-[#16a34a] underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    部品リストをダウンロード
                  </a>
                  <span className="text-sm leading-[1.42] text-[#32383e]">
                    して記入後にファイルをドラッグ、もしくは
                  </span>
                  <span className="text-sm leading-[1.42] text-[#16a34a] underline">ファイルから選択</span>
                </div>
                <p className="text-center text-sm leading-[1.42] text-[#9fa6ad]">
                  拡張子 .xls / .xlsx（ファイル容量10MBまで）
                </p>
              </>
            )}
          </div>
        )}
      </div>
      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
      {validationError && <p className="text-sm text-red-600">{validationError}</p>}
    </>
  )
}

export default PartsListUpload
